import { useCallback, useEffect, useState } from 'react';
import {
  Tile,
  Tag,
  Button,
  TextArea,
  InlineNotification,
  Loading,
} from '@carbon/react';
import { Email, Renew, Send, Checkmark } from '@carbon/icons-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  listContactSubmissions,
  replyToContactSubmission,
  markContactSubmissionReviewed,
} from '@/lib/contactApi';
import { getFriendlyApiError } from '@/lib/publicError';

const statusTag = {
  new: 'blue',
  reviewed: 'gray',
  replied: 'green',
};

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-AU', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

const ContactReviewPage = () => {
  const { getToken } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);

  const selected = submissions.find((item) => item.id === selectedId) || null;

  const loadSubmissions = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const data = await listContactSubmissions(getToken);
      setSubmissions(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(getFriendlyApiError(err, 'We could not load submissions. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  useEffect(() => {
    if (!selectedId && submissions.length > 0) {
      setSelectedId(submissions[0].id);
    }
  }, [submissions, selectedId]);

  const updateSubmissionInList = (updated) => {
    setSubmissions((current) =>
      current.map((item) => (item.id === updated.id ? updated : item))
    );
  };

  const handleSelect = (submission) => {
    setSelectedId(submission.id);
    setReplyMessage('');
    setNotice(null);
    setError('');
  };

  const handleMarkReviewed = async () => {
    if (!selected) return;

    setSending(true);
    setError('');

    try {
      const updated = await markContactSubmissionReviewed(selected.id, getToken);
      updateSubmissionInList(updated);
      setNotice({ kind: 'success', title: 'Marked as reviewed' });
    } catch (err) {
      setError(getFriendlyApiError(err, 'We could not update this submission. Please try again.'));
    } finally {
      setSending(false);
    }
  };

  const handleSendReply = async (event) => {
    event.preventDefault();
    if (!selected || !replyMessage.trim()) return;

    setSending(true);
    setError('');
    setNotice(null);

    try {
      const updated = await replyToContactSubmission(selected.id, replyMessage.trim(), getToken);
      updateSubmissionInList(updated);
      setReplyMessage('');
      setNotice({
        kind: 'success',
        title: 'Reply sent',
        subtitle: `Email sent to ${selected.email} via Resend.`,
      });
    } catch (err) {
      setError(getFriendlyApiError(err, 'We could not send your reply. Please try again.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="app-page contact-review-page" data-testid="contact-review-page">
      <div className="section-header contact-review-header">
        <div>
          <Tag type="blue">ADMIN</Tag>
          <h2 className="section-title">Contact submissions</h2>
          <p className="section-description">
            Review homepage contact form messages and reply by email.
          </p>
        </div>
        <Button kind="ghost" renderIcon={Renew} onClick={loadSubmissions} disabled={loading}>
          Refresh
        </Button>
      </div>

      {notice && (
        <InlineNotification
          kind={notice.kind}
          title={notice.title}
          subtitle={notice.subtitle}
          onClose={() => setNotice(null)}
          className="contact-review-notice"
        />
      )}

      {error && (
        <InlineNotification
          kind="error"
          title="Something went wrong"
          subtitle={error}
          onClose={() => setError('')}
          className="contact-review-notice"
        />
      )}

      {loading ? (
        <div className="contact-review-loading">
          <Loading withOverlay={false} description="Loading submissions..." />
        </div>
      ) : submissions.length === 0 ? (
        <Tile className="contact-review-empty">
          <Email size={32} />
          <p>No contact form submissions yet.</p>
        </Tile>
      ) : (
        <div className="contact-review-layout">
          <Tile className="contact-review-list">
            <h3 className="contact-review-list__title">Inbox ({submissions.length})</h3>
            <ul className="contact-review-items">
              {submissions.map((submission) => {
                const isActive = submission.id === selectedId;

                return (
                  <li key={submission.id}>
                    <button
                      type="button"
                      className={`contact-review-item${isActive ? ' contact-review-item--active' : ''}`}
                      onClick={() => handleSelect(submission)}
                    >
                      <div className="contact-review-item__top">
                        <strong>{submission.name}</strong>
                        <Tag type={statusTag[submission.status] || 'gray'} size="sm">
                          {submission.status || 'new'}
                        </Tag>
                      </div>
                      <span className="contact-review-item__email">{submission.email}</span>
                      <span className="contact-review-item__preview">
                        {submission.message?.slice(0, 90)}
                        {submission.message?.length > 90 ? '…' : ''}
                      </span>
                      <span className="contact-review-item__date">{formatDate(submission.createdAt)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </Tile>

          {selected && (
            <Tile className="contact-review-detail">
              <div className="contact-review-detail__header">
                <div>
                  <h3>{selected.name}</h3>
                  <p>{selected.email}{selected.phone ? ` · ${selected.phone}` : ''}</p>
                  <p className="contact-review-detail__meta">
                    Received {formatDate(selected.createdAt)}
                    {selected.lastReplyAt ? ` · Last reply ${formatDate(selected.lastReplyAt)}` : ''}
                  </p>
                </div>
                {selected.status !== 'reviewed' && selected.status !== 'replied' && (
                  <Button
                    kind="tertiary"
                    size="sm"
                    renderIcon={Checkmark}
                    disabled={sending}
                    onClick={handleMarkReviewed}
                  >
                    Mark reviewed
                  </Button>
                )}
              </div>

              <div className="contact-review-message">
                <h4>Message</h4>
                <p>{selected.message}</p>
              </div>

              {selected.replies?.length > 0 && (
                <div className="contact-review-history">
                  <h4>Previous replies</h4>
                  {selected.replies.map((reply, index) => (
                    <div key={`${selected.id}-reply-${index}`} className="contact-review-history__item">
                      <span>{formatDate(reply.sentAt)}</span>
                      <p>{reply.message}</p>
                    </div>
                  ))}
                </div>
              )}

              <form className="contact-review-reply" onSubmit={handleSendReply}>
                <TextArea
                  id="contact-reply-message"
                  labelText="Reply via email"
                  placeholder="Write your response to the sender..."
                  value={replyMessage}
                  onChange={(event) => setReplyMessage(event.target.value)}
                  rows={6}
                  required
                />
                <Button
                  type="submit"
                  renderIcon={Send}
                  disabled={sending || !replyMessage.trim()}
                >
                  {sending ? 'Sending…' : 'Send reply'}
                </Button>
              </form>
            </Tile>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactReviewPage;
